"use client";

import { motion } from "framer-motion";
import { Fragment, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { CampaignMetric } from "@/modules/campaign/metrics";
import { Message } from "@/modules/marketing/features/message";
import type { MessageProps } from "@/modules/marketing/homepage/types";
import { EASE } from "./constants";

export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border border-border bg-background shadow-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}

// Canonical status chip used across every scene: a neutral outline pill whose
// only color is a single indicator dot — never a tinted background.
export function DotBadge({
  dot,
  className,
  children,
}: {
  dot?: string | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Badge variant="outline" className={cn("bg-background font-medium", className)}>
      {dot ? <span className={cn("mr-1.5 size-1.5 shrink-0 rounded-full", dot)} /> : null}
      {children}
    </Badge>
  );
}

export type Stat = {
  name: string;
  value: number;
  formatter: (v: number) => string;
  numberClassName?: string;
};

// Rollup metric strip. On mobile it scrolls horizontally instead of overflowing
// the viewport; from sm up it sits as a compact, content-width pill.
export function StatStrip({ stats }: { stats: Stat[] }) {
  return (
    <div className="max-w-full overflow-x-auto sm:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex w-fit items-center gap-3 rounded-md border border-border bg-background px-3 py-1.5">
        {stats.map((s, i) => (
          <Fragment key={s.name}>
            {i > 0 ? <Separator orientation="vertical" className="h-6 shrink-0" /> : null}
            <div className="shrink-0">
              <CampaignMetric
                name={s.name}
                value={s.value}
                formatter={s.formatter}
                size="sm"
                numberClassNames={cn("text-base", s.numberClassName)}
              />
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export function ChatBubbles({
  messages,
  reduce,
  startDelay = 0.2,
  gap = 0.9,
}: {
  messages: MessageProps[];
  reduce: boolean;
  startDelay?: number;
  gap?: number;
}) {
  return (
    <div className="flex flex-col">
      {messages.map((m, i) => (
        <motion.div
          key={m.id}
          initial={reduce ? false : { opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, ease: EASE, delay: reduce ? 0 : startDelay + i * gap }}
        >
          <Message {...m} index={i} className="shadow-sm" />
        </motion.div>
      ))}
    </div>
  );
}
